import React, { useCallback, useState, useEffect, useRef } from 'react';
import { useDispatch, useMappedState } from 'redux-react-hook';
import loadImage from 'blueimp-load-image';
import _map from 'lodash/map';
import _isEmpty from 'lodash/isEmpty';
import _some from 'lodash/some';
import _trim from 'lodash/trim';
import _size from 'lodash/size';
import _last from 'lodash/last';
import _split from 'lodash/split';
import _random from 'lodash/random';
import { usePrevious } from 'common/customHooks';
import { sendImageUploadSizeLimitReachedEventToGoogleAnalytics } from 'common/googleAnalytics';
import {
  addPhoto,
  deletePhoto,
  updatePhotoDescription,
  uploadPhotoToS3,
  deletePhotoFromS3,
} from 'redux/actions/consultation/answer';
import { Error } from 'styles/blocks/error';
import { ColumnDiv } from 'styles/elements/div';
import { VALIDATION_MESSAGES } from 'common/validationMessages';
import { InfoBox } from 'styles/elements/infoBoxes/info';
import { STRINGS } from 'config';
import loadingSpinner from 'assets/primaryCare/icons/spinner.gif';
import { v4 as uuidv4 } from 'uuid';
import { consultationSelector } from 'redux/selectors/consultation';
import { DropZone } from './dropzone';
import { Preview } from './preview';
import { NextButton } from '../nextButton';

const ERROR = {
  GENERIC: 0,
  MAX_SIZE_EXCEEDED: 1,
  MISSING_DESCRIPTION: 2,
  MAX_LENGTH_DESC_EXCEEDED: 3,
};

const MAX_PHOTO_UPLOAD = 4;
const MAX_FILE_SIZE_BYTES = 8 * 1024 * 1024; // 8MB

const { getTemplatedValidation } = VALIDATION_MESSAGES;
const { S3_IMAGE_SOLUTION } = STRINGS.TAGS.QUESTION;

export function PhotoUpload({ onClickNextQuestion, questionId, questionContainerRef, questionTags }) {
  const mapState = useCallback(state => ({
    photos: state.consultationReducer.photos,
    sessionId: state.consultationReducer.sessionId,
    photoAttachmentInProgress: consultationSelector(state).photoAttachmentInProgress,
  }), []);
  const { photos, sessionId, photoAttachmentInProgress } = useMappedState(mapState);
  const dispatch = useDispatch();

  const [error, setError] = useState({});
  const [isFileTypeInvalid, setIsFileTypeInvalid] = useState(false);
  const [isFileMaxSizeExceeded, setIsFileMaxSizeExceeded] = useState(false);
  const newPhotoDescInputRef = useRef();
  const spinnerImage = { fileBytes: `${STRINGS.S3_BUCKET}${loadingSpinner}`, fileDescription: '', fileName: 'spinner', optimisticAdd: true };

  const preventRefresh = useCallback(e => {
    if (photoAttachmentInProgress) {
      e.preventDefault();
    }
  }, [photoAttachmentInProgress]);

  useEffect(() => {
    window.addEventListener('beforeunload', preventRefresh);

    return () => {
      window.removeEventListener('beforeunload', preventRefresh);
    };
  }, [dispatch, preventRefresh]);

  useEffect(() => {
    // Scroll question container div back to top so errors are visible
    if (error.code && questionContainerRef !== null) {
      // eslint-disable-next-line no-param-reassign
      questionContainerRef.current.scrollTop = 0;
    }
  }, [error.code, questionContainerRef]);

  const validateDescription = useCallback(() => {
    const isPhotoDescriptionEmpty = _some(photos[questionId], photo => _isEmpty(_trim(photo.fileDescription)));
    if (isPhotoDescriptionEmpty) {
      setError({ code: ERROR.MISSING_DESCRIPTION, message: VALIDATION_MESSAGES.PHOTO.MISSING_DESCRIPTION });
      return false;
    }
    const isDescriptionMaxLengthExceeded = _some(photos[questionId], photo => _size(photo.fileDescription) > 500);
    if (isDescriptionMaxLengthExceeded) {
      setError({
        code: ERROR.MAX_LENGTH_DESC_EXCEEDED,
        message: getTemplatedValidation(VALIDATION_MESSAGES.GENERIC.ANSWER_LENGTH_GT_MAX, { maxLength: 500 }),
      });
      return false;
    }
    setError({});
    return true;
  }, [photos, questionId]);
  const handleOnClickNextQuestion = () => {
    const isDescriptionValid = validateDescription();
    return (isDescriptionValid && !photoAttachmentInProgress) ? onClickNextQuestion() : null;
  };
  const previousPhotosLength = usePrevious(_size(photos[questionId]));
  useEffect(() => {
    if (error.code && previousPhotosLength && previousPhotosLength > _size(photos[questionId])) {
      validateDescription();
    }
    if (!error.code && previousPhotosLength < _size(photos[questionId]) && newPhotoDescInputRef && newPhotoDescInputRef.current) {
      newPhotoDescInputRef.current.focus();
    }
  }, [error.code, validateDescription, previousPhotosLength, photos, questionId, newPhotoDescInputRef]);

  const useS3ImageSolution = questionTags?.includes(S3_IMAGE_SOLUTION);

  const onAddPhoto = files => {
    setError({});
    setIsFileTypeInvalid(false);
    setIsFileMaxSizeExceeded(false);
    const numberOfUploads = _size(photos[questionId]);
    _map(files, (file, index) => {
      if (index >= (MAX_PHOTO_UPLOAD - numberOfUploads)) {
        return;
      }
      // Checks for file names that do not include .jpeg .png or .jpg and sets validation.
      if (!/(\.jpg|\.jpeg|\.png)$/i.test(file.name)) {
        setIsFileTypeInvalid(true);
        return;
      }
      if (useS3ImageSolution && file.size > MAX_FILE_SIZE_BYTES) {
        setIsFileMaxSizeExceeded(true);
        sendImageUploadSizeLimitReachedEventToGoogleAnalytics();
        return;
      }
      const photoId = uuidv4();
      if (useS3ImageSolution) {
        dispatch(uploadPhotoToS3(file, photoId, spinnerImage));
        return;
      }
      const maxSize = 600;
      const fileType = file.type || 'image/jpeg';
      loadImage(file, canvas => {
        if (canvas.type === 'error') {
          setError({ code: ERROR.GENERIC, message: VALIDATION_MESSAGES.PHOTO.GENERIC });
        } else {
          const fileExtension = _last(_split(file.name, '.'));
          const randomString = _random(0.1, 0.9).toString(36).substr(2, 4);
          const sessionShortened = _split(sessionId, '-')[0];
          dispatch(addPhoto({
            fileBytes: canvas.toDataURL(),
            fileType: fileType,
            fileDescription: '',
            fileName: `${sessionShortened}-${randomString}.${fileExtension}`,
            photoId,
          }));
        }
      }, {
        orientation: true,
        canvas: true,
        maxWidth: maxSize,
        maxHeight: maxSize,
      });
    });
  };

  const onDeletePhoto = useCallback(
    (id, photo) => (useS3ImageSolution ? dispatch(deletePhotoFromS3(id, photo)) : dispatch(deletePhoto(id))),
    [dispatch, useS3ImageSolution],
  );

  return (
    <ColumnDiv>
      {error.message && <Error>{error.message}</Error>}
      {_map(photos[questionId], (photo, key) => {
        return !photo.optimisticDelete && (
          <Preview
            photo={photo}
            key={`photo-preview-${key}-${photo.fileName}`}
            id={key}
            onDeletePhoto={onDeletePhoto}
            updatePhotoDescription={(id, description) => dispatch(updatePhotoDescription(id, description))}
            isInvalid={
                (error.code === ERROR.MISSING_DESCRIPTION && _isEmpty(_trim(photo.fileDescription)))
                || (error.code === ERROR.MAX_LENGTH_DESC_EXCEEDED && _size(photo.fileDescription) > 500)
            }
            textareaRef={newPhotoDescInputRef}
          />
        );
      })}
      {_size(photos[questionId]) >= MAX_PHOTO_UPLOAD ? (
        <InfoBox>{getTemplatedValidation(VALIDATION_MESSAGES.PHOTO.MAX_PHOTOS_EXCEEDED, { maxPhoto: MAX_PHOTO_UPLOAD })}</InfoBox>
      ) : (
        <DropZone onAddPhoto={onAddPhoto} isFileTypeInvalid={isFileTypeInvalid} isFileMaxSizeExceeded={isFileMaxSizeExceeded} />
      )}
      <NextButton
        dataAutomation={`nextButton-${questionId}`}
        onClick={handleOnClickNextQuestion}
        loadingText={!_isEmpty(photos[questionId]) && ['Uploading your photos...', 'Shouldn\'t be much longer...']}
      />
    </ColumnDiv>
  );
}
